import { Button } from '@/components/Button';
import { Tag } from '@/components/Tag';
import Input from '@/components/Input';
import Select from '@/components/Select';
import { TAG_COLORS, COLOR_OPTIONS } from '@/constants/tagColors';
import type { AgentApiTagsRecord } from '@/types/serializers';
import { useState, useEffect, useMemo } from 'react';
import type { TagCondition } from '../types/TagCondition';
import { CANDIDATE_FIELD_OPTIONS, buildCustomFieldOptions } from '../types/TagCondition';
import TagConditionRow from './TagConditionRow';
import useFetchTagConditions from '../hooks/useFetchTagConditions';
import useCreateTagCondition from '../hooks/useCreateTagCondition';
import useUpdateTagCondition from '../hooks/useUpdateTagCondition';
import useDeleteTagCondition from '../hooks/useDeleteTagCondition';
import useBulkUnchain from '../hooks/useBulkUnchain';
import useBulkChain from '../hooks/useBulkChain';
import useFetchCandidateProfilesCustomFields from '@/features/agent/candidates/api/useFetchCandidateProfilesCustomFields';
import { MaterialIcon } from '@/components/MaterialIcon';
import Modal from '@/components/Modal';
import { BlockButton } from '@/components/BlockButton';
import { showSuccessToast, showErrorToast } from '../../job_change_processes/lib/toast';

interface Props {
  editingTag: AgentApiTagsRecord | null;
  tagName: string;
  selectedColor: { name: string; value: string; label: string } | null;
  onTagNameChange: (value: string) => void;
  onColorChange: (color: { name: string; value: string; label: string }) => void;
  onDeleteTag: (tag: AgentApiTagsRecord) => void;
  onUpdateTag: () => void;
  isUpdating: boolean;
}

export default function TagSettingsForm({
  editingTag,
  tagName,
  selectedColor,
  onTagNameChange,
  onColorChange,
  onDeleteTag,
  onUpdateTag,
  isUpdating,
}: Props) {
  const [isConditionOpen, setIsConditionOpen] = useState(false);
  const [localConditions, setLocalConditions] = useState<TagCondition[]>([]);
  const [deletedConditionIds, setDeletedConditionIds] = useState<number[]>([]);
  const [isUnchainDialogOpen, setIsUnchainDialogOpen] = useState(false);
  const [isChainDialogOpen, setIsChainDialogOpen] = useState(false);

  // API連携用のhooks
  const { data: conditionsData } = useFetchTagConditions(editingTag?.id || null);
  const createCondition = useCreateTagCondition();
  const updateCondition = useUpdateTagCondition();
  const deleteCondition = useDeleteTagCondition();
  const bulkUnchain = useBulkUnchain();
  const bulkChain = useBulkChain();
  const { data: customFieldsData } = useFetchCandidateProfilesCustomFields();

  // 求職者の実カラム項目 + カスタムフィールド項目（condition_column は "field_<id>" 形式）をマージ
  const fieldOptions = useMemo(() => {
    const customFieldOptions = buildCustomFieldOptions(customFieldsData?.records ?? []);
    return [...CANDIDATE_FIELD_OPTIONS, ...customFieldOptions];
  }, [customFieldsData]);

  // サーバーから取得した条件をローカルステートに同期
  useEffect(() => {
    if (conditionsData?.records) {
      setLocalConditions(conditionsData.records);
    } else {
      setLocalConditions([]);
    }
  }, [conditionsData]);

  // タグが変更されたら削除リストをリセット
  useEffect(() => {
    if (editingTag) {
      setDeletedConditionIds([]);
    }
  }, [editingTag?.id]);

  // 保存済み条件の件数。一括紐付け/解除ジョブはサーバー保存済みの条件しか見ないため活性判定に使う
  const savedConditionCount = conditionsData?.records?.length ?? 0;

  // 未保存の条件変更があるか。未保存のまま一括処理を実行すると古い条件で判定され結果が変わってしまう
  const hasUnsavedConditionChanges = useMemo(() => {
    if (deletedConditionIds.length > 0) return true;
    if (localConditions.some((c) => !c.id)) return true;

    const savedConditions = conditionsData?.records ?? [];
    return localConditions.some((c) => {
      const saved = savedConditions.find((s) => s.id === c.id);
      if (!saved) return true;
      return (
        saved.condition_column !== c.condition_column ||
        saved.condition_type !== c.condition_type ||
        saved.condition_content !== c.condition_content
      );
    });
  }, [conditionsData, localConditions, deletedConditionIds]);

  const isBulkActionDisabled = savedConditionCount === 0 || hasUnsavedConditionChanges;

  // 条件を追加（まずローカルに追加、保存はしない）
  const handleAddCondition = () => {
    if (!editingTag) return;

    const newCondition: TagCondition = {
      condition_column: CANDIDATE_FIELD_OPTIONS[0].value,
      condition_content: '',
      condition_type: 'eq',
    };

    setLocalConditions([...localConditions, newCondition]);
  };

  // 条件を更新（ローカル更新のみ）
  // 未保存の新規行は id が未採番（undefined）で複数行が同値になりうるため、
  // id 一致ではなく index で対象行を一意に特定して更新する。
  const handleUpdateCondition = (index: number, condition: TagCondition) => {
    setLocalConditions(localConditions.map((c, i) => (i === index ? condition : c)));
  };

  // 条件を削除（削除対象としてマーク、またはローカルから削除）
  // 更新と同様、未保存行は id で区別できないため行位置で特定する
  const handleDeleteCondition = (index: number) => {
    if (!editingTag) return;

    const target = localConditions[index];
    if (!target) return;

    if (target.id) {
      setDeletedConditionIds([...deletedConditionIds, target.id]);
    }
    setLocalConditions(localConditions.filter((_, i) => i !== index));
  };

  // 「適用する」ボタンで全ての変更を保存
  const handleApply = async () => {
    if (!editingTag) return;

    try {
      // まずタグ自体を更新（戻り値が Promise の場合も考慮し、条件の削除/作成より先に完了させる）
      await Promise.resolve(onUpdateTag());

      // 削除対象の条件を削除
      for (const conditionId of deletedConditionIds) {
        await deleteCondition.mutateAsync({
          custom_tag_id: editingTag.id,
          id: conditionId,
        });
      }

      // 新規条件を保存（IDがないもの。キーワード未入力の行は除外）
      const newConditions = localConditions.filter(
        (c) => !c.id && c.condition_content.trim() !== ''
      );
      for (const condition of newConditions) {
        await createCondition.mutateAsync({
          custom_tag_id: editingTag.id,
          condition_column: condition.condition_column,
          condition_content: condition.condition_content,
          condition_type: condition.condition_type,
        });
      }

      // 既存条件を更新（IDがあるもの）
      const existingConditions = localConditions.filter((c) => c.id);
      for (const condition of existingConditions) {
        if (!condition.id) continue;
        await updateCondition.mutateAsync({
          custom_tag_id: editingTag.id,
          id: condition.id,
          condition_column: condition.condition_column,
          condition_content: condition.condition_content,
          condition_type: condition.condition_type,
        });
      }
    } catch (error) {
      showErrorToast('条件の保存に失敗しました');
      console.error('Failed to apply conditions:', error);
      return;
    }

    // 適用後、削除リストをクリア
    setDeletedConditionIds([]);
  };

  // 紐付け解除のダイアログでの確定処理
  const handleConfirmUnchain = async () => {
    if (!editingTag) return;
    try {
      await bulkUnchain.mutateAsync({ custom_tag_id: editingTag.id });
      showSuccessToast('紐づけの解除を開始します');
      setIsUnchainDialogOpen(false);
    } catch (error) {
      showErrorToast('紐づけの解除に失敗しました');
      console.error('Failed to unchain:', error);
    }
  };

  // 紐付けのダイアログでの確定処理
  const handleConfirmChain = async () => {
    if (!editingTag) return;
    try {
      await bulkChain.mutateAsync({ custom_tag_id: editingTag.id });
      showSuccessToast('紐づけを開始します');
      setIsChainDialogOpen(false);
    } catch (error) {
      showErrorToast('紐づけに失敗しました');
      console.error('Failed to chain:', error);
    }
  };

  return (
    <div className="flex-1 rounded-sm bg-white p-lg shadow-layer1">
      <div className="flex flex-col gap-lg">
        {/* ヘッダー */}
        <div className="flex items-center justify-between">
          <h3 className="text-heading-md text-main">タグ設定</h3>
          <Button
            onClick={() => editingTag && onDeleteTag(editingTag)}
            color="danger-secondary"
            size="md"
            disabled={!editingTag}
          >
            <MaterialIcon name="delete" filled className="text-xl" />
            タグを削除する
          </Button>
        </div>

        {/* タグ設定フォーム */}
        <div className="flex flex-col gap-sm">
          {/* タグタイトル */}
          <div>
            <label className="mb-xs flex items-center gap-xs">
              <span className="text-heading-xxs text-main">タグタイトル</span>
              <Tag color="danger" size="sm">
                必須
              </Tag>
            </label>
            <Input
              value={tagName}
              onChange={(e) => onTagNameChange(e.target.value)}
              placeholder="タグタイトル（15文字以内）"
              maxLength={15}
              className="w-full"
              disabled={!editingTag}
            />
          </div>

          {/* カラー選択 */}
          <div className="w-[218px]">
            <label className="mb-xs block text-heading-xxs text-main">カラー選択</label>
            <Select
              items={COLOR_OPTIONS}
              value={selectedColor?.label || ''}
              onValueChange={(value) => {
                const color = TAG_COLORS.find((c) => c.label === value);
                if (color) onColorChange(color);
              }}
              className="w-full"
              disabled={!editingTag}
            />
          </div>

          {/* 自動タグ付け条件設定 */}
          <div className="w-full">
            <Button
              color="tertiary"
              size="md"
              onClick={() => setIsConditionOpen(!isConditionOpen)}
              disabled={!editingTag}
              className="h-[42px] gap-1"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={`transition-transform ${isConditionOpen ? 'rotate-180' : ''}`}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              自動タグ付け条件を設定する
            </Button>

            {/* 条件設定セクション（展開時） */}
            {isConditionOpen && editingTag && (
              <div className="mt-4 flex flex-col gap-4">
                {/* 一括操作ボタン */}
                <div className="flex flex-col gap-2">
                  <div className="flex gap-4">
                    <Button
                      color="secondary"
                      size="sm"
                      disabled={isBulkActionDisabled}
                      className={`gap-1 ${isBulkActionDisabled ? 'opacity-50' : ''}`}
                      onClick={() => setIsChainDialogOpen(true)}
                    >
                      <MaterialIcon name="autorenew" className="text-base" />
                      該当する求職者全てに紐付けする
                    </Button>
                    <Button
                      color="danger-secondary"
                      size="sm"
                      disabled={isBulkActionDisabled}
                      className={`gap-1 ${isBulkActionDisabled ? 'opacity-50' : ''}`}
                      onClick={() => setIsUnchainDialogOpen(true)}
                    >
                      <MaterialIcon name="remove" className="text-base" />
                      該当しない求職者の紐付けを解除する
                    </Button>
                  </div>
                  {isBulkActionDisabled && (
                    <p className="text-xs text-danger">
                      {hasUnsavedConditionChanges
                        ? '未保存の条件があります。「適用する」で保存してから実行してください。'
                        : '一括操作には保存済みの条件が必要です。条件を追加して「適用する」で保存してください。'}
                    </p>
                  )}
                </div>

                {/* 条件リスト */}
                {localConditions.length > 0 && (
                  <div className="flex flex-col gap-4">
                    {localConditions.map((condition, index) => (
                      <TagConditionRow
                        key={condition.id || `new-${index}`}
                        condition={condition}
                        fieldOptions={fieldOptions}
                        onUpdate={(updated: TagCondition) => handleUpdateCondition(index, updated)}
                        onDelete={() => handleDeleteCondition(index)}
                      />
                    ))}
                  </div>
                )}

                {/* 条件を追加するボタン */}
                <Button
                  color="secondary"
                  size="md"
                  className="h-[42px] w-full gap-1"
                  onClick={handleAddCondition}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.8333 9.16667H15.8333V10.8333H10.8333V15.8333H9.16667V10.8333H4.16667V9.16667H9.16667V4.16667H10.8333V9.16667Z"
                      fill="currentColor"
                    />
                  </svg>
                  条件を追加する
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* 適用ボタン */}
        <div className="flex justify-end">
          <Button
            onClick={handleApply}
            color="primary"
            size="lg"
            disabled={!editingTag || !tagName.trim() || isUpdating}
          >
            {isUpdating ? '更新中...' : '適用する'}
          </Button>
        </div>
      </div>

      {/* 紐付け確認ダイアログ */}
      <Modal id="chain-dialog" isShow={isChainDialogOpen}>
        <div className="w-[640px] rounded bg-white">
          <div className="flex h-[53px] items-center gap-1 border-b border-solid border-border px-6">
            <p className="text-base font-bold text-gray-900">求職者の紐づけ</p>
          </div>
          <div className="border-b border-solid border-border p-6">
            <p className="text-sm text-gray-900">本当に該当する求職者全てに紐付けしますか？</p>
          </div>
          <div className="flex h-[74px] items-center justify-end gap-4 px-6">
            <div className="w-[94px]">
              <BlockButton
                type="button"
                colorType="white"
                text="キャンセル"
                onClick={() => setIsChainDialogOpen(false)}
                disabled={bulkChain.isPending}
              />
            </div>
            <div className="w-[108px]">
              <BlockButton
                type="button"
                colorType="blue"
                text={bulkChain.isPending ? '処理中...' : '紐付けする'}
                onClick={handleConfirmChain}
                disabled={bulkChain.isPending}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* 紐付け解除確認ダイアログ */}
      <Modal id="unchain-dialog" isShow={isUnchainDialogOpen}>
        <div className="w-[640px] rounded bg-white">
          <div className="flex h-[53px] items-center gap-1 border-b border-solid border-border px-6">
            <p className="text-base font-bold text-gray-900">紐付けの解除</p>
          </div>
          <div className="border-b border-solid border-border p-6">
            <p className="text-sm text-gray-900">本当に該当しない求職者の紐付けを解除しますか？</p>
          </div>
          <div className="flex h-[74px] items-center justify-end gap-4 px-6">
            <div className="w-[94px]">
              <BlockButton
                type="button"
                colorType="white"
                text="キャンセル"
                onClick={() => setIsUnchainDialogOpen(false)}
                disabled={bulkUnchain.isPending}
              />
            </div>
            <div className="w-[134px]">
              <BlockButton
                type="button"
                colorType="red"
                text={bulkUnchain.isPending ? '処理中...' : '紐付け解除する'}
                onClick={handleConfirmUnchain}
                disabled={bulkUnchain.isPending}
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
